import { RPC_AUTHENTICATION, type ChainInfo } from '@safe-global/safe-gateway-typescript-sdk'

/**
 * Resolves the RPC endpoint to use for a chain, without requiring the user to enter one.
 *
 * Kept in a separate file, sibling to lumiaChains.ts and lumiaDeployments.ts, so the change to
 * each upstream call site (the read-only provider setup in useInitWeb3.ts, and the welcome screen
 * that decides whether to ask for an endpoint) stays a single import and a single call.
 *
 * The user's own stored value always wins: the settings page still lets anyone override the
 * endpoint, and this resolver must not get in the way of that. An empty stored string does not
 * count as an override (LoadRPCUrl's own validation never saves one, but setRpc({chainId}) with
 * no rpc field, or hand-built state, still can) and deliberately falls through to publicRpcUri
 * below, the same as having no stored value at all.
 *
 * Failing that, the chain's publicRpcUri is used, but only when its authentication is one of the
 * two spellings that mean "no key needed": the SDK's own RPC_AUTHENTICATION.NO_AUTHENTICATION, or
 * the 'NO_AUTH' sentinel useMagicNetwork.ts writes onto custom chains built from a magic link
 * (`'NO_AUTH' as RPC_AUTHENTICATION`, not an SDK enum member). This is a two-value allow-list, not
 * a deny-list on RPC_AUTHENTICATION.API_KEY_PATH alone, because RPC_AUTHENTICATION.UNKNOWN (the
 * gateway does not know whether authentication is required) is reachable through imported chain
 * configurations, not just in theory, and an unverified endpoint must not be handed out as if it
 * were known-usable. An API_KEY_PATH endpoint, such as the Infura URLs several upstream chains
 * ship with no key configured here, is excluded the same way: it is simply not one of the two
 * allowed spellings.
 */
export const resolveRpcUrl = (
  chain: Pick<ChainInfo, 'chainId' | 'publicRpcUri'> | undefined,
  rpcMap: Record<string, string> | undefined,
): string | undefined => {
  if (!chain) return undefined

  const userRpc = rpcMap?.[chain.chainId]
  if (userRpc) return userRpc

  const publicRpc = chain.publicRpcUri
  const authentication: string | undefined = publicRpc?.authentication
  const isUsableAuthentication = authentication === RPC_AUTHENTICATION.NO_AUTHENTICATION || authentication === 'NO_AUTH'

  if (publicRpc?.value && isUsableAuthentication) {
    return publicRpc.value
  }

  return undefined
}
